import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import api from '@/api';

export function OTPVerification({ identifier, maskedIdentifier, onVerified, onBack }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleVerify = useCallback(async (otpCode) => {
    if (otpCode.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const result = await api.verifyOTP({ email: identifier, code: otpCode });
      onVerified(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
      setCode('');
    } finally { setLoading(false); }
  }, [identifier, onVerified]);

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await api.resendOTP({ email: identifier });
      setResendCooldown(30);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally { setResending(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
      className="w-full max-w-md" data-testid="otp-verification-screen">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-5">
          <ShieldCheck size={28} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify your email</h2>
        <p className="text-gray-500 text-base">
          We sent a 6-digit code to<br />
          <span className="font-semibold text-gray-700">{maskedIdentifier}</span>
        </p>
      </div>
      <div className="flex justify-center mb-6">
        <InputOTP maxLength={6} value={code} onChange={(val) => { setCode(val); if (val.length === 6) handleVerify(val); }} disabled={loading} data-testid="otp-input">
          <InputOTPGroup>
            {[0,1,2,3,4,5].map(i => (
              <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl font-bold border-gray-300 rounded-xl" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {loading && <div className="flex justify-center mb-4"><Loader2 size={24} className="animate-spin text-gray-400" /></div>}
      <AnimatePresence>
        {error && (
          <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="text-red-500 text-sm text-center mb-4" data-testid="otp-error">{error}</motion.p>
        )}
      </AnimatePresence>
      <div className="text-center mb-6">
        <p className="text-sm text-gray-400 mb-1">Didn't receive the code?</p>
        <button data-testid="resend-otp-btn" onClick={handleResend} disabled={resendCooldown > 0 || resending}
          className="text-sm font-semibold text-gray-900 hover:underline disabled:text-gray-300 disabled:no-underline transition-colors">
          {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
        </button>
      </div>
      <button data-testid="otp-back-btn" onClick={onBack}
        className="w-full py-3 rounded-2xl border border-gray-200 text-gray-600 text-base font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 transition-all">
        <ArrowLeft size={16} /> Back to sign up
      </button>
    </motion.div>
  );
}
